import { useEffect } from "react";
import { useRouteError } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { logError } from "~/helpers/datadog";
import { logger } from "~/helpers/logger";
import { paths } from "~/router/paths";
import {
  Actions,
  Container,
  Description,
  HomeLink,
  ReloadButton,
  Title,
} from "./RouteErrorBoundary.style";

/**
 * Rendered by react-router when a route element throws during render.
 * Replaces the router's built-in "Unexpected Application Error!" page with a
 * translated, recoverable screen, and reports the crash to Datadog (render
 * errors are swallowed by the boundary, so window.onerror never sees them).
 */
const RouteErrorBoundary = () => {
  const error = useRouteError();
  const { t } = useTranslation();

  useEffect(() => {
    logger.error("Route error boundary triggered", error);
    logError("react_render_crash", error, {
      path: window.location.pathname,
      source: "route_error_boundary",
    });
  }, [error]);

  return (
    <Container role="alert">
      <Title>{t("error_boundary.title")}</Title>
      <Description>{t("error_boundary.description")}</Description>
      <Actions>
        {/* Full page loads on purpose: after a hard render crash the React
            tree may be corrupted, so client-side navigation isn't safe. */}
        <ReloadButton onClick={() => window.location.reload()}>
          {t("error_boundary.reload")}
        </ReloadButton>
        <HomeLink href={paths.app.home}>{t("error_boundary.go_home")}</HomeLink>
      </Actions>
    </Container>
  );
};

export default RouteErrorBoundary;
